import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { AnimationOptions } from 'ngx-lottie';

import * as FileSaver from 'file-saver';
import { LyricsService } from 'src/app/services/lyrics/lyrics.service';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { LocalLyricsVersion, LyricsVersion, LyricsDocument, RhymeData } from 'src/app/models/lyrics.model';
import { PlayerService } from 'src/app/services/music-player/player.service';
import { StreamState } from 'src/app/models/stream';
import { BeatsService } from 'src/app/services/beats-service/beats.service';
import { WATERMARK_BASE64 } from './watermark-base64';
import { Ga4trackingService } from 'src/app/services/ga4Tracking/ga4tracking.service';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';

@Component({
    selector: 'ipm-lyrics-editor',
    templateUrl: './lyrics-editor.component.html',
    styleUrls: ['./lyrics-editor.component.scss']
})
export class LyricsEditorComponent implements OnInit, OnDestroy {
    @Input() beat: any;  // BeatListModel from popup data
    @Input() initialVersion: number;  // Optional — switch to this version on load
    @ViewChild('lyricsTextarea') textareaRef: ElementRef<HTMLTextAreaElement>;

    // Validation constants
    readonly MAX_VERSIONS = 10;
    readonly MAX_LYRICS_CHARS = 50000;
    readonly MAX_TITLE_CHARS = 40;

    // State
    versions: LocalLyricsVersion[] = [];
    activeVersion = 1;
    currentLyricsText = '';
    currentTitle = '';
    isLoading = true;
    isSaving = false;
    beatHasLyricsOnServer = false;

    // Play/pause state + seekbar
    isCurrentBeatPlaying = false;
    currentPlayingBeatId: string | null = null;
    isBeatLoading = false;
    playerState: StreamState = {
        playing: false,
        readableCurrentTime: '00:00',
        readableDuration: '00:00',
        duration: undefined,
        currentTime: undefined,
        volume: 1,
        canplay: false,
        error: false,
        ended: false
    };

    // Inline status message (replaces toast notifications)
    statusMessage = '';
    statusType: 'success' | 'error' | 'info' | '' = '';
    private statusTimeout: any;

    // Auto-save state
    autoSaveEnabled = false;
    autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
    private autoSave$ = new Subject<void>();
    private autoSaveStatusTimeout: any;

    // Inline delete confirmation
    versionToDelete: number | null = null;
    isDeleting = false;
    private deleteConfirmTimeout: any;

    // Rhyme state
    rhymeData: RhymeData | null = null;
    rhymeSearchWord = '';
    autoSearchEnabled = false;
    isSearchingRhymes = false;
    rhymeLoaderOptions: AnimationOptions = {
        path: '/assets/data/search-loader.json',
        loop: true,
        autoplay: true
    };
    private wordSubject$ = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private lyricsService: LyricsService,
        public commonService: CommonService,
        private playerService: PlayerService,
        private beatService: BeatsService,
        private router: Router,
        private ga4Tracking: Ga4trackingService,
        private browserStorageService: BrowserStorageService
    ) { }

    ngOnInit() {
        this.loadBeatLyrics();
        this.setupRhymePipeline();
        this.setupAutoSavePipeline();
        this.subscribeToPlayerState();
        this.enrichBeatDataIfNeeded();
    }

    /** Fetch full beat data if the beat object is missing producer/preview info */
    private enrichBeatDataIfNeeded() {
        if (this.beat?._id && !this.beat?.producer) {
            this.beatService.getBeatList(
                { filter: { beat_id: this.beat._id }, page: { offset: 0, limit: 1 } } as any,
                false
            ).subscribe(beats => {
                if (beats?.length) {
                    Object.assign(this.beat, beats[0]);
                }
            });
        }
    }

    // ── PLAYER STATE SUBSCRIPTION ──
    subscribeToPlayerState() {
        this.playerService.getState().pipe(
            takeUntil(this.destroy$)
        ).subscribe((state: StreamState) => {
            this.playerState = state;
            this.updatePlayingState();
        });

        this.beatService.currentPlayBeat.pipe(
            takeUntil(this.destroy$)
        ).subscribe(beat => {
            this.currentPlayingBeatId = beat ? beat._id : null;
            this.updatePlayingState();
        });
    }

    private updatePlayingState() {
        this.isCurrentBeatPlaying = !!(this.playerState.playing && this.currentPlayingBeatId === this.beat?._id);

        if (this.currentPlayingBeatId !== this.beat?._id || this.playerState.playing || this.playerState.canplay || this.playerState.error) {
            this.isBeatLoading = false;
        }
    }

    /** Seekbar drag end — seek the audio */
    onSeekChange(event: any) {
        this.playerService.seekTo(event.value);
    }

    // ── PLAY/PAUSE TOGGLE ──
    togglePlayPause() {
        // If the current playing beat is not this beat, trigger the full music player with this beat
        if (this.currentPlayingBeatId !== this.beat?._id) {
            this.isBeatLoading = true;
            if (this.beat?.preview) {
                this.startPlayback(this.beat);
            } else if (this.beat?._id) {
                // Beat opened from My Lyrics — preview URL missing, fetch full beat data first
                this.beatService.getBeatList(
                    { filter: { beat_id: this.beat._id }, page: { offset: 0, limit: 1 } } as any,
                    false
                ).subscribe(beats => {
                    if (beats?.length) {
                        // Merge the full beat data back so subsequent plays don't re-fetch
                        Object.assign(this.beat, beats[0]);
                        this.startPlayback(this.beat);
                    } else {
                        this.isBeatLoading = false; // reset on error/empty
                    }
                }, () => {
                    this.isBeatLoading = false; // reset on error
                });
            }
        } else {
            this.playerService.isCurrentPlayChangeState();
        }
    }

    private startPlayback(beat: any) {
        this.beatService.playerBeatsList.next([beat]);
        this.beatService.currentPlayBeat.next(beat);
        this.beatService.isMusicPlayOpen.next(true);
    }

    // ── NAVIGATION (clickable title / producer) ──
    goToBeatPage() {
        const sn = this.beat?.beatSerialNumber || this.beat?.serial_number;
        if (sn) {
            this.commonService.closePopUp();
            this.router.navigate([`/bt-${sn}`]);
        }
    }

    goToProducerPage() {
        const handle = this.beat?.created_by?.handle;
        if (handle) {
            this.commonService.closePopUp();
            this.router.navigate(['/', handle]);
        }
    }

    /** Returns the last_modified string for the currently active version, converted to IST */
    get activeLastModified(): string {
        const v = this.getActiveVersion();
        if (!v?.lastModified) return '';
        return this.lyricsService.convertUtcToIst(v.lastModified);
    }

    // ── LOAD (1 API call) ──
    loadBeatLyrics() {
        this.isLoading = true;
        this.lyricsService.getBeatLyrics(this.beat._id).subscribe(
            res => {
                const doc: LyricsDocument = res?.data;
                if (doc?.lyrics_versions?.length > 0) {
                    this.beatHasLyricsOnServer = true;
                    this.versions = doc.lyrics_versions.map((v: LyricsVersion) => ({
                        version: v.version,
                        title: v.title || '',
                        lyrics: v.lyrics || '',
                        existsOnServer: true,
                        isDirty: false,
                        originalLyrics: v.lyrics || '',
                        originalTitle: v.title || '',
                        lastModified: v.last_modified || ''
                    }));

                    // Ensure version 1 is always first, rest in creation order
                    this.versions.sort((a, b) => {
                        if (a.version === 1) return -1;
                        if (b.version === 1) return 1;
                        return a.version - b.version;
                    });

                    // If initialVersion is specified and exists, switch to it
                    const startVersion = this.initialVersion
                        ? this.versions.find(v => v.version === this.initialVersion)
                        : null;
                    const first = startVersion || this.versions[0];
                    this.activeVersion = first.version;
                    this.currentLyricsText = first.lyrics;
                    this.currentTitle = first.title;
                } else {
                    this.initEmptyLyrics();
                }
                this.isLoading = false;
            },
            () => {
                this.initEmptyLyrics();
                this.isLoading = false;
            }
        );
    }

    initEmptyLyrics() {
        this.beatHasLyricsOnServer = false;
        this.versions = [{
            version: 1, title: '', lyrics: '',
            existsOnServer: false, isDirty: false, originalLyrics: '', originalTitle: ''
        }];
        this.activeVersion = 1;
        this.currentLyricsText = '';
        this.currentTitle = '';
        // No server call — version stays local until user types content
    }

    // ── SAVE (1 API call, with guard) ──
    saveLyrics(isAutoSave = false) {
        // Track manual save events only
        if (!isAutoSave) {
            const userRole = this.browserStorageService?.getCurrentRoles() || 'guest';
            const userId = this.browserStorageService?.getUserId() || 'guest';
            this.ga4Tracking.sendEvent('save_lyrics', {
                category: 'Beats',
                label: 'Engagement',
                action: 'click',
                userRole,
                userId,
            });
        }

        if (!this.beat || !this.beat._id || this.beat._id.length !== 24) {
            this.showStatus('Invalid beat ID', 'error');
            return;
        }

        const v = this.getActiveVersion();
        if (!v || this.isSaving) return;

        this.syncCurrentText();

        // Guard: require at least title or lyrics to have content
        if (v.title.trim() === '' && v.lyrics.trim() === '') {
            if (!isAutoSave) {
                this.showStatus('Add a title or lyrics to save', 'info');
            }
            return;
        }

        if (v.lyrics.length > this.MAX_LYRICS_CHARS) {
            this.showStatus(`Lyrics cannot exceed ${this.MAX_LYRICS_CHARS} characters`, 'error');
            return;
        }

        this.isSaving = true;
        if (isAutoSave) this.autoSaveStatus = 'saving';

        if (v.existsOnServer) {
            // UPDATE existing version
            this.lyricsService.updateVersion(this.beat._id, v.version, { lyrics: v.lyrics, title: v.title })
                .subscribe(
                    (res) => {
                        v.isDirty = false;
                        v.originalLyrics = v.lyrics;
                        v.originalTitle = v.title;
                        if (res?.data?.lyrics_versions) {
                            const serverV = res.data.lyrics_versions.find((sv: any) => sv.version === v.version);
                            if (serverV?.last_modified) {
                                v.lastModified = serverV.last_modified;
                            }
                        }
                        this.isSaving = false;
                        if (isAutoSave) {
                            this.setAutoSaveStatus('saved');
                        } else {
                            this.showStatus('Lyrics saved', 'success');
                        }
                        this.lyricsService.lyricsUpdated$.next(this.beat._id);
                    },
                    () => {
                        this.isSaving = false;
                        if (isAutoSave) {
                            this.setAutoSaveStatus('error');
                        } else {
                            this.showStatus('Failed to save. Please try again.', 'error');
                        }
                    }
                );
        } else {
            // CREATE new version on server
            this.lyricsService.createLyrics({ beat_id: this.beat._id, lyrics: v.lyrics, title: v.title })
                .subscribe(
                    (res) => {
                        this.beatHasLyricsOnServer = true;
                        v.existsOnServer = true;
                        v.isDirty = false;
                        v.originalLyrics = v.lyrics;
                        v.originalTitle = v.title;
                        this.syncVersionFromCreateResponse(v, res);
                        this.isSaving = false;
                        if (isAutoSave) {
                            this.setAutoSaveStatus('saved');
                        } else {
                            this.showStatus('Lyrics saved', 'success');
                        }
                        this.lyricsService.lyricsUpdated$.next(this.beat._id);
                    },
                    () => {
                        this.isSaving = false;
                        if (isAutoSave) {
                            this.setAutoSaveStatus('error');
                        } else {
                            this.showStatus('Failed to save. Please try again.', 'error');
                        }
                    }
                );
        }
    }

    /**
     * After a create API response, sync the server-assigned version number
     * back to the local version object.
     */
    private syncVersionFromCreateResponse(localVersion: LocalLyricsVersion, res: any) {
        if (!res?.data?.lyrics_versions?.length) return;

        // The create response returns only the newly created version(s)
        // Find the one matching our title/lyrics
        const serverVersions = res.data.lyrics_versions;
        const match = serverVersions.find((sv: any) => sv.title === localVersion.title)
            || serverVersions[serverVersions.length - 1]; // fallback: last one

        if (match) {
            const oldVersion = localVersion.version;
            localVersion.version = match.version;
            localVersion.lastModified = match.last_modified || '';

            // Update activeVersion if it was pointing to the old number
            if (this.activeVersion === oldVersion) {
                this.activeVersion = match.version;
            }
        }
    }

    // ── CREATE NEW VERSION (1 API call — create on server immediately) ──
    createNewVersion() {
        if (this.versions.length >= this.MAX_VERSIONS) {
            this.showStatus(`Maximum ${this.MAX_VERSIONS} versions allowed`, 'error');
            return;
        }
        if (this.isSaving) return;

        this.syncCurrentText();

        // If current version is dirty and has content, save it first
        const currentV = this.getActiveVersion();
        if (currentV?.isDirty && (currentV.title.trim() !== '' || currentV.lyrics.trim() !== '')) {
            this.isSaving = true;
            const save$ = currentV.existsOnServer
                ? this.lyricsService.updateVersion(this.beat._id, currentV.version, { lyrics: currentV.lyrics, title: currentV.title })
                : this.lyricsService.createLyrics({ beat_id: this.beat._id, lyrics: currentV.lyrics, title: currentV.title });
            save$.subscribe(
                (res) => {
                    currentV.isDirty = false;
                    currentV.originalLyrics = currentV.lyrics;
                    currentV.originalTitle = currentV.title;
                    if (!currentV.existsOnServer) {
                        currentV.existsOnServer = true;
                        this.beatHasLyricsOnServer = true;
                        this.syncVersionFromCreateResponse(currentV, res);
                    }
                    this.isSaving = false;
                    this.createNewVersionLocally();
                },
                () => { this.isSaving = false; this.showStatus('Failed to save current version', 'error'); }
            );
        } else {
            this.createNewVersionLocally();
        }
    }

    /** Create a new empty version locally — no server call until user adds content and saves */
    private createNewVersionLocally() {
        const nextVersion = this.getNextVersionNumber();
        this.versions.push({
            version: nextVersion,
            title: '',
            lyrics: '',
            existsOnServer: false,
            isDirty: false,
            originalLyrics: '',
            originalTitle: '',
            lastModified: ''
        });
        this.switchVersion(nextVersion);
    }

    /** Find the smallest positive integer not in the current versions list */
    private getNextVersionNumber(): number {
        const used = new Set(this.versions.map(v => v.version));
        let n = 1;
        while (used.has(n)) n++;
        return n;
    }

    // ── SWITCH VERSION (0 API calls) ──
    switchVersion(versionNum: number) {
        this.syncCurrentText();
        this.activeVersion = versionNum;
        const v = this.getActiveVersion();
        this.currentLyricsText = v ? v.lyrics : '';
        this.currentTitle = v ? v.title : '';
    }

    // ── DELETE VERSION — Inline confirmation ──
    requestDeleteVersion(versionNum: number, event: Event) {
        event.stopPropagation();
        const v = this.versions.find(x => x.version === versionNum);
        if (!v) return;

        // Show inline confirmation on the capsule
        this.versionToDelete = versionNum;
        clearTimeout(this.deleteConfirmTimeout);
        // Auto-cancel after 5 seconds
        this.deleteConfirmTimeout = setTimeout(() => {
            this.versionToDelete = null;
        }, 5000);
    }

    /** Cancel inline delete confirmation */
    cancelDelete(event: Event) {
        event.stopPropagation();
        this.versionToDelete = null;
        clearTimeout(this.deleteConfirmTimeout);
    }

    /** Confirm and perform the version deletion */
    confirmDelete(versionNum: number, event: Event) {
        event.stopPropagation();
        clearTimeout(this.deleteConfirmTimeout);
        this.versionToDelete = null;
        this.performVersionDeletion(versionNum);
    }

    /** Perform the actual version deletion */
    performVersionDeletion(versionNum: number): void {
        const v = this.versions.find(x => x.version === versionNum);
        if (!v) return;

        const doRemove = () => {
            this.versions = this.versions.filter(x => x.version !== versionNum);
            if (this.versions.length === 0) {
                // All versions deleted — create a fresh blank version
                this.versions = [{
                    version: 1, title: '', lyrics: '', originalTitle: '', originalLyrics: '',
                    isDirty: false, existsOnServer: false, lastModified: ''
                }];
                this.activeVersion = 1;
                this.currentLyricsText = '';
                this.currentTitle = '';
            } else if (this.activeVersion === versionNum) {
                this.activeVersion = this.versions[0].version;
                this.currentLyricsText = this.versions[0].lyrics;
                this.currentTitle = this.versions[0].title;
            }
        };

        if (v.existsOnServer) {
            this.isDeleting = true;
            this.lyricsService.deleteVersion(this.beat._id, versionNum).subscribe(
                () => {
                    this.isDeleting = false;
                    doRemove();
                    this.showStatus('Version deleted', 'success');
                    this.lyricsService.lyricsUpdated$.next(this.beat._id);
                },
                () => {
                    this.isDeleting = false;
                    this.showStatus('Failed to delete version', 'error');
                }
            );
        } else {
            doRemove();
        }
    }

    // ── CLOSE & SAVE (batch creates, individual updates) ──
    closeAndSave() {
        if (this.isSaving) return;
        this.syncCurrentText();

        // Filter out versions that are dirty AND have content (don't save empty local versions)
        const dirtyVersions = this.versions.filter(v =>
            v.isDirty && (v.title.trim() !== '' || v.lyrics.trim() !== '' || v.existsOnServer)
        );
        if (dirtyVersions.length === 0) {
            this.commonService.closePopUp();
            return;
        }

        this.isSaving = true;

        // Separate into updates (existsOnServer) and creates (new)
        const toUpdate = dirtyVersions.filter(v => v.existsOnServer);
        const toCreate = dirtyVersions.filter(v => !v.existsOnServer);

        const updateCalls$ = toUpdate.map(v =>
            this.lyricsService.updateVersion(this.beat._id, v.version, { lyrics: v.lyrics, title: v.title })
        );

        // Batch all new versions into a single create call using the versions array API
        let createCall$;
        if (toCreate.length > 0) {
            const versionsPayload = toCreate.map(v => ({ title: v.title, lyrics: v.lyrics }));
            createCall$ = this.lyricsService.createLyricsBatch({
                beat_id: this.beat._id,
                versions: versionsPayload
            });
        } else {
            createCall$ = of(null);
        }

        const allCalls$ = updateCalls$.length > 0
            ? forkJoin([...updateCalls$, createCall$])
            : forkJoin([createCall$]);

        allCalls$.subscribe(
            (results) => {
                dirtyVersions.forEach(v => {
                    v.existsOnServer = true;
                    v.isDirty = false;
                    v.originalLyrics = v.lyrics;
                    v.originalTitle = v.title;
                });
                this.beatHasLyricsOnServer = true;

                if (toCreate.length > 0) {
                    const createRes = results[results.length - 1];
                    if (createRes?.data?.lyrics_versions) {
                        this.syncBatchCreateVersions(toCreate, createRes.data.lyrics_versions);
                    }
                }

                this.isSaving = false;
                this.lyricsService.lyricsUpdated$.next(this.beat._id);
                this.commonService.closePopUp();
            },
            () => {
                this.isSaving = false;
                this.showStatus('Failed to save. Please try again.', 'error');
            }
        );
    }

    /** Sync server-assigned version numbers for batch-created versions */
    private syncBatchCreateVersions(localVersions: LocalLyricsVersion[], serverVersions: any[]) {
        // Match by title since that's the most reliable field
        localVersions.forEach(local => {
            const match = serverVersions.find((sv: any) => sv.title === local.title);
            if (match) {
                local.version = match.version;
                local.lastModified = match.last_modified || '';
            }
        });
    }

    // ── RHYME PIPELINE (debounced) ──
    setupRhymePipeline() {
        this.wordSubject$.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            switchMap(word => {
                if (word.length < 2) {
                    this.isSearchingRhymes = false;
                    return of(null);
                }
                this.isSearchingRhymes = true;
                return this.lyricsService.getRhymes(word).pipe(
                    map(res => {
                        const data = res?.data as RhymeData;
                        if (data) {
                            if (data.primary) data.primary = data.primary.slice(0, 80);
                            if (data.secondary) data.secondary = data.secondary.slice(0, 80);
                            if (data.freestyle) data.freestyle = data.freestyle.slice(0, 80);
                        }
                        return data;
                    }),
                    catchError(() => of(null))
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe(d => { this.rhymeData = d; this.isSearchingRhymes = false; });
    }

    /** Called on cursor move / keyup in textarea — only fires if autoSearchEnabled */
    onCursorMove(event: any) {
        if (!this.autoSearchEnabled) return;
        const ta = event.target as HTMLTextAreaElement;
        const word = this.extractWordAtPosition(ta.value, ta.selectionStart);
        this.wordSubject$.next(word || '');
    }

    /** Manual search bar input */
    onRhymeSearchInput() {
        const word = this.rhymeSearchWord.trim();
        this.wordSubject$.next(word);
    }

    onLyricsInput(event: any) {
        if (this.currentLyricsText.length > this.MAX_LYRICS_CHARS) {
            this.currentLyricsText = this.currentLyricsText.substring(0, this.MAX_LYRICS_CHARS);
        }

        const v = this.getActiveVersion();
        if (v) { v.lyrics = this.currentLyricsText; v.isDirty = v.lyrics !== v.originalLyrics || v.title !== v.originalTitle; }
        this.onCursorMove(event);
        this.autoSave$.next();
    }

    onTitleInput() {
        const v = this.getActiveVersion();
        if (v) {
            v.title = this.currentTitle;
            v.isDirty = v.lyrics !== v.originalLyrics || v.title !== v.originalTitle;
        }
        this.autoSave$.next();
    }

    // ── DOWNLOAD LYRICS AS PDF ──
    async downloadLyrics() {
        const { jsPDF, GState } = await import('jspdf');
        const userRole = this.browserStorageService?.getCurrentRoles() || 'guest';
        const userId = this.browserStorageService?.getUserId() || 'guest';
        this.ga4Tracking.sendEvent('download_lyrics', {
            category: 'Beats',
            label: 'Engagement',
            action: 'click',
            userRole,
            userId,
        });

        const v = this.getActiveVersion();
        if (!v) return;

        const title = v.title || 'Untitled';
        const lyrics = v.lyrics || '';

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        const maxWidth = pageWidth - margin * 2;

        // Watermark helper — centered on page at 10% opacity
        const addWatermark = () => {
            try {
                doc.saveGraphicsState();
                doc.setGState(new GState({ opacity: 0.10 }));
                const wmWidth = 70;
                const wmHeight = 112;
                const wmX = (pageWidth - wmWidth) / 2;
                const wmY = (pageHeight - wmHeight) / 2;
                doc.addImage(WATERMARK_BASE64, 'PNG', wmX, wmY, wmWidth, wmHeight);
                doc.restoreGraphicsState();
            } catch { /* GState not supported — skip watermark */ }
        };

        // First page watermark
        addWatermark();

        // Build beat page URL
        const serialNum = this.beat?.beatSerialNumber || this.beat?.serial_number;
        const beatUrl = serialNum
            ? `https://beat22.com/bt-${serialNum}`
            : '';

        // Title 
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(30, 30, 30);
        doc.text(title, pageWidth / 2, 25, { align: 'center' });

        // Beat info (clickable link to beat page)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(100, 80, 200);
        const beatInfo = this.beat?.title ? `Beat: ${this.beat.title}` : '';
        if (beatInfo && beatUrl) {
            const infoWidth = doc.getTextWidth(beatInfo);
            const infoX = (pageWidth - infoWidth) / 2;
            doc.textWithLink(beatInfo, infoX, 33, { url: beatUrl });
            doc.setDrawColor(100, 80, 200);
            doc.setLineWidth(0.3);
            doc.line(infoX, 33.5, infoX + infoWidth, 33.5);
        } else if (beatInfo) {
            doc.text(beatInfo, pageWidth / 2, 33, { align: 'center' });
        }

        // Separator line
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, 38, pageWidth - margin, 38);

        // Lyrics body
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(30, 30, 30);
        const lines = doc.splitTextToSize(lyrics, maxWidth);
        let y = 46;
        const lineHeight = 7;

        for (const line of lines) {
            if (y + lineHeight > pageHeight - margin) {
                doc.addPage();
                addWatermark();
                y = margin;
            }
            doc.text(line, margin, y);
            y += lineHeight;
        }

        const fileName = `${title.replace(/[^a-zA-Z0-9 ]/g, '').trim() || 'lyrics'}.pdf`;
        const pdfBlob = doc.output('blob');
        FileSaver.saveAs(pdfBlob, fileName);
    }

    insertRhyme(word: string) {
        const ta = this.textareaRef?.nativeElement;
        if (!ta) return;
        const start = ta.selectionStart;
        const before = this.currentLyricsText.substring(0, start);
        const after = this.currentLyricsText.substring(start);
        this.currentLyricsText = before + word + ' ' + after;
        const v = this.getActiveVersion();
        if (v) { v.lyrics = this.currentLyricsText; v.isDirty = true; }
        setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + word.length + 1; });
        this.autoSave$.next();
    }

    // ── Helpers ──
    getActiveVersion(): LocalLyricsVersion | undefined {
        return this.versions.find(v => v.version === this.activeVersion);
    }

    private syncCurrentText() {
        const v = this.getActiveVersion();
        if (v) {
            v.lyrics = this.currentLyricsText;
            v.title = this.currentTitle;
            v.isDirty = v.lyrics !== v.originalLyrics || v.title !== v.originalTitle;
        }
    }

    private extractWordAtPosition(text: string, pos: number): string {
        const left = text.substring(0, pos).replace(/[^a-zA-Z]+$/, '');
        const right = text.substring(pos).replace(/^[^a-zA-Z]+/, '');
        const wordLeft = left.match(/[a-zA-Z]+$/)?.[0] || '';
        const wordRight = right.match(/^[a-zA-Z]+/)?.[0] || '';
        return wordLeft + wordRight;
    }

    // ── INLINE STATUS MESSAGE SYSTEM ──
    showStatus(message: string, type: 'success' | 'error' | 'info', duration = 4000) {
        clearTimeout(this.statusTimeout);
        this.statusMessage = message;
        this.statusType = type;
        if (duration > 0) {
            this.statusTimeout = setTimeout(() => {
                this.statusMessage = '';
                this.statusType = '';
            }, duration);
        }
    }

    dismissStatus() {
        clearTimeout(this.statusTimeout);
        this.statusMessage = '';
        this.statusType = '';
    }

    // ── AUTO-SAVE PIPELINE (2s debounce, industry standard) ──
    private setupAutoSavePipeline() {
        this.autoSave$.pipe(
            debounceTime(2000),
            filter(() => this.autoSaveEnabled && !this.isSaving),
            filter(() => {
                const v = this.getActiveVersion();
                return !!v?.isDirty && (v.title.trim() !== '' || v.lyrics.trim() !== '');
            }),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.saveLyrics(true);
        });
    }

    /** Update auto-save status indicator with auto-reset */
    private setAutoSaveStatus(status: 'saved' | 'error') {
        this.autoSaveStatus = status;
        clearTimeout(this.autoSaveStatusTimeout);
        if (status === 'saved') {
            this.autoSaveStatusTimeout = setTimeout(() => {
                this.autoSaveStatus = 'idle';
            }, 3000);
        }
    }

    ngOnDestroy() {
        clearTimeout(this.statusTimeout);
        clearTimeout(this.autoSaveStatusTimeout);
        clearTimeout(this.deleteConfirmTimeout);
        this.destroy$.next();
        this.destroy$.complete();
    }
}
